import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../../hooks/useAuth'
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '../../../services/supplierService'
import { showAlert, showConfirm } from '../../../services/dialogService'
import InfoButton from '../../../components/InfoButton/InfoButton'
import Loader from '../../../components/Loader/Loader'
import EditVendor from './EditVendor'
import '../Section.css'
import './NumbersPlease.css'

const VENDOR_TYPES = ['Photographer', 'Videographer', 'Cake', 'Catering', 'Florist', 'DJ', 'Planner', 'Venue', 'Hair & Makeup', 'Transport', 'Other']

function NumbersPlease() {
  const { user, loading: authLoading } = useAuth()
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddRow, setShowAddRow] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState(null)
  const [costSort, setCostSort] = useState(null) // null | 'asc' | 'desc'
  const [newSupplier, setNewSupplier] = useState({
    category: 'Photographer',
    name: '',
    contact_name: '',
    phone: '',
    email: '',
    web: '',
    arrival_time: '',
    end_time: '',
    meal_required: false,
    total_cost_package: '',
    inclusions_notes: '',
    booked: false
  })

  useEffect(() => {
    if (!user) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await getSuppliers(user.id, false)
        setSuppliers(data)
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [user])

  const filteredSuppliers = useMemo(() => {
    let list = suppliers.filter(s => !s.booked)
    if (costSort === 'asc') {
      list = [...list].sort((a, b) => (parseFloat(a.total_cost_package || a.rate || 0)) - (parseFloat(b.total_cost_package || b.rate || 0)))
    } else if (costSort === 'desc') {
      list = [...list].sort((a, b) => (parseFloat(b.total_cost_package || b.rate || 0)) - (parseFloat(a.total_cost_package || a.rate || 0)))
    }
    return list
  }, [suppliers, costSort])

  const handleCostSort = () => {
    setCostSort(s => s === null ? 'asc' : s === 'asc' ? 'desc' : null)
  }

  const handleBookedChange = async (supplier, checked) => {
    try {
      await updateSupplier(supplier.id, { booked: checked })
      setSuppliers(prev => prev.map(s => s.id === supplier.id ? { ...s, booked: checked } : s))
    } catch (err) {
      console.error(err)
      await showAlert('Error updating supplier')
    }
  }

  const handleAddSupplier = async (e) => {
    e.preventDefault()
    if (!newSupplier.name.trim()) {
      await showAlert('Business name is required')
      return
    }
    try {
      const created = await createSupplier(user.id, {
        ...newSupplier,
        total_cost_package: newSupplier.total_cost_package ? parseFloat(newSupplier.total_cost_package) : null,
        booked: false
      })
      setSuppliers(prev => [...prev, created])
      setShowAddRow(false)
      setNewSupplier({
        category: 'Photographer',
        name: '',
        contact_name: '',
        phone: '',
        email: '',
        web: '',
        arrival_time: '',
        end_time: '',
        meal_required: false,
        total_cost_package: '',
        inclusions_notes: '',
        booked: false
      })
    } catch (err) {
      console.error(err)
      await showAlert('Error adding supplier')
    }
  }

  const handleDelete = async (supplier) => {
    const ok = await showConfirm('Delete this supplier?', { title: 'Delete supplier', confirmLabel: 'Delete', cancelLabel: 'Cancel', variant: 'danger' })
    if (!ok) return
    try {
      await deleteSupplier(supplier.id)
      setSuppliers(prev => prev.filter(s => s.id !== supplier.id))
    } catch (err) {
      console.error(err)
      await showAlert('Error deleting supplier')
    }
  }

  const formatCurrency = (v) => {
    const n = parseFloat(v || 0)
    return isNaN(n) ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  }

  if (authLoading || loading) return <Loader />

  return (
    <div className="dashboard-section supplier-numbers-please">
      <h1>
        SUPPLIER MANAGER — NUMBERS, PLEASE
        <InfoButton
          title="Numbers, please"
          content={
            <div>
              <p>
                All your maybe-someday suppliers go here. Compare, judge (a little), and choose your favourites.
              </p>
              <p><strong>Features:</strong></p>
              <ul>
                <li>Add multiple supplier quotes for the same service</li>
                <li>Filter by cost: cheapest to most expensive or vice versa</li>
                <li>Track contact info, arrival times, and inclusions</li>
              </ul>
              <p><strong>Columns:</strong></p>
              <ul>
                <li><strong>Booked</strong> — Tick the box to move that supplier to &quot;The chosen ones&quot; (your booked team).</li>
                <li><strong>Total Cost package</strong> — Click the column header to sort by cost (ascending or descending).</li>
                <li><strong>Actions</strong> — Edit details or delete a supplier.</li>
              </ul>
            </div>
          }
        />
      </h1>
      <div className="supplier-table-header">
        <h2>Vendors</h2>
        <div className="supplier-table-actions">
          <button onClick={() => showAlert('Export coming soon')}>Export</button>
          <button onClick={() => window.print()}>Print</button>
        </div>
      </div>

      <div className="supplier-table-wrap">
        <table className="supplier-table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Business Name</th>
              <th>Contact Name</th>
              <th>Phone / Email</th>
              <th>Web</th>
              <th>Arrival Time</th>
              <th>End Time</th>
              <th>Meal Required</th>
              <th className="supplier-sortable" onClick={handleCostSort}>
                Total Cost package {costSort === 'asc' && '↑'} {costSort === 'desc' && '↓'}
              </th>
              <th>Inclusions/Notes</th>
              <th>Booked</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSuppliers.map((s) => (
              <tr key={s.id}>
                <td>{s.category || '—'}</td>
                <td>{s.name || s.business_name || '—'}</td>
                <td>{s.contact_name || '—'}</td>
                <td>{[s.phone, s.email].filter(Boolean).join(' / ') || '—'}</td>
                <td>{s.web ? <a href={s.web} target="_blank" rel="noopener noreferrer">{s.web}</a> : '—'}</td>
                <td>{s.arrival_time || '—'}</td>
                <td>{s.end_time || '—'}</td>
                <td>{s.meal_required ? 'Yes' : 'No'}</td>
                <td>{formatCurrency(s.total_cost_package || s.rate)}</td>
                <td>{s.inclusions_notes || s.notes || '—'}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={!!s.booked}
                    onChange={(e) => handleBookedChange(s, e.target.checked)}
                    title="Mark as booked → moves to The chosen ones"
                  />
                </td>
                <td className="supplier-td-actions">
                  <button className="supplier-btn-edit" onClick={() => setEditingSupplier(s)}>Edit</button>
                  <button className="supplier-btn-delete" onClick={() => handleDelete(s)}>Delete</button>
                </td>
              </tr>
            ))}
            {showAddRow && (
              <tr className="supplier-add-row">
                <td>
                  <select
                    value={newSupplier.category}
                    onChange={(e) => setNewSupplier({ ...newSupplier, category: e.target.value })}
                  >
                    {VENDOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    placeholder="Business name"
                    value={newSupplier.name}
                    onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    placeholder="Contact"
                    value={newSupplier.contact_name}
                    onChange={(e) => setNewSupplier({ ...newSupplier, contact_name: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    placeholder="Phone / Email"
                    value={[newSupplier.phone, newSupplier.email].join(' / ')}
                    onChange={(e) => {
                      const parts = e.target.value.split('/').map(x => x.trim())
                      setNewSupplier({ ...newSupplier, phone: parts[0] || '', email: parts[1] || '' })
                    }}
                  />
                </td>
                <td>
                  <input
                    type="url"
                    placeholder="Web"
                    value={newSupplier.web}
                    onChange={(e) => setNewSupplier({ ...newSupplier, web: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    placeholder="Arrival"
                    value={newSupplier.arrival_time}
                    onChange={(e) => setNewSupplier({ ...newSupplier, arrival_time: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    placeholder="End"
                    value={newSupplier.end_time}
                    onChange={(e) => setNewSupplier({ ...newSupplier, end_time: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={newSupplier.meal_required}
                    onChange={(e) => setNewSupplier({ ...newSupplier, meal_required: e.target.checked })}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    placeholder="0"
                    step="0.01"
                    value={newSupplier.total_cost_package}
                    onChange={(e) => setNewSupplier({ ...newSupplier, total_cost_package: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    placeholder="Notes"
                    value={newSupplier.inclusions_notes}
                    onChange={(e) => setNewSupplier({ ...newSupplier, inclusions_notes: e.target.value })}
                  />
                </td>
                <td>—</td>
                <td>
                  <button onClick={handleAddSupplier}>Save</button>
                  <button onClick={() => setShowAddRow(false)}>Cancel</button>
                </td>
              </tr>
            )}
            {filteredSuppliers.length === 0 && !showAddRow && (
              <tr>
                <td colSpan="12" className="supplier-empty">
                  No suppliers yet. Click &quot;Add vendor&quot; to add one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!showAddRow && (
        <button className="supplier-add-vendor-btn" onClick={() => setShowAddRow(true)}>
          Add vendor
        </button>
      )}

      {editingSupplier && (
        <div
          className="supplier-edit-modal"
          onClick={async (e) => {
            if (e.target !== e.currentTarget) return
            const ok = await showConfirm('Leave without saving? Your changes to this vendor will not be saved.', { title: 'Discard changes?', confirmLabel: 'Leave', cancelLabel: 'Stay', variant: 'danger' })
            if (ok) setEditingSupplier(null)
          }}
        >
          <div className="supplier-edit-modal-content" onClick={(e) => e.stopPropagation()}>
            <EditVendor
              supplier={editingSupplier}
              onClose={() => setEditingSupplier(null)}
              onSave={(updated) => {
                setSuppliers(prev => prev.map(s => s.id === updated.id ? updated : s))
                setEditingSupplier(null)
              }}
              onDelete={() => {
                setSuppliers(prev => prev.filter(s => s.id !== editingSupplier.id))
                setEditingSupplier(null)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default NumbersPlease
